import { content, order } from 'core';
import { ORDER_PAYMENT_VIEWS, SHIPPING_MODEL } from 'core/domains';
import { PAYMENT_PLAN_TYPE } from 'core/services/bff';
import { useParams } from 'react-router';
import {
  Layout,
  OrderSuccessWrapper,
  OrderSuccessSkeleton,
  Text,
  Container,
  ButtonSolid,
  ButtonGhost,
  Link,
} from 'ui/components';
import { OrderSuccesButton, OrderSuccessTitle } from 'ui/components/templates/OrderSuccess';
import { ContentBuilder } from 'ui/containers/ContentBuilder';

import {
  orderSuccessBoletoWidgetsMapper,
  orderSuccessBoletoInstallmentsWidgetsMapper,
} from './BankSlip';
import { HeaderOrderSuccess } from './components';
import {
  orderCreditCardCanceledMapper,
  orderCreditCardPaymentProcessMapper,
  orderCreditCardPaymentCompletedMapper,
} from './CreditCard/widgets';
import { useEventGoogleAnalytics } from './hooks';
import { orderOtherMapper } from './PayWithdrawals';
import { orderPixMapper } from './Pix';

import type { OrderSuccessProps } from './OrderSuccess.d';

export const widgetViewsMapperPayment = {
  [ORDER_PAYMENT_VIEWS.CREDIT_CARD_CANCELED]: orderCreditCardCanceledMapper,
  [ORDER_PAYMENT_VIEWS.CREDIT_CARD_PROCESSING]: orderCreditCardPaymentCompletedMapper,
  [ORDER_PAYMENT_VIEWS.CREDIT_CARD_PROCESS]: orderCreditCardPaymentProcessMapper,
  [ORDER_PAYMENT_VIEWS.GENERIC]: orderOtherMapper,
  [ORDER_PAYMENT_VIEWS.BOLETO]: orderSuccessBoletoWidgetsMapper,
  [ORDER_PAYMENT_VIEWS.BOLETO_INSTALLMENTS]: orderSuccessBoletoInstallmentsWidgetsMapper,
  [ORDER_PAYMENT_VIEWS.OTHER]: orderOtherMapper,
  [ORDER_PAYMENT_VIEWS.PIX]: orderPixMapper,
} as const;

const OrderSuccessContainer = ({ match, sectionType }: OrderSuccessProps) => {
  const { orderCode } = useParams<{ orderCode: string }>();
  const [handleClickToHome, trackUserClicks] = useEventGoogleAnalytics();

  const {
    data: { components, options },
    isError: isPrismicError,
    isLoading,
  } = content.hooks.useContent(sectionType);

  const {
    data: { code, paymentMethod, shippingModel = { shippingModelCode: '' } },
    isError: isOrderError,
    isLoading: isOrderLoading,
    view,
  } = order.hooks.useOrder();

  const isPickupStore = shippingModel?.shippingModelCode === SHIPPING_MODEL.PICKUP_STORE;
  const isPaymentCreditCard = paymentMethod === PAYMENT_PLAN_TYPE.CREDIT_CARD;

  if (isLoading || isOrderLoading) {
    return (
      <OrderSuccessWrapper>
        <OrderSuccessSkeleton />
      </OrderSuccessWrapper>
    );
  }

  if (isPrismicError || isOrderError) {
    return (
      <div data-testid="order-error-page">
        <HeaderOrderSuccess />

        <Container size="1/2">
          <OrderSuccessTitle
            isPickupStore={isPickupStore}
            orderCode={code ?? orderCode ?? ''}
            {...options}
          />

          <Text color="medium-light-1" variant="body-large-short" textAlign="center">
            Você pode visualizar mais informações na área de Pedidos. Em breve, você receberá também
            as informações por e-mail.
          </Text>

          <OrderSuccesButton.ButtonWrapper data-margin-top="sp-6" data-margin-bottom="sp-4">
            <Link to="/pedidos" onClick={trackUserClicks}>
              <ButtonSolid stretch>Visualizar pedido</ButtonSolid>
            </Link>
          </OrderSuccesButton.ButtonWrapper>

          <ButtonGhost stretch onClick={handleClickToHome}>
            Continuar comprando
          </ButtonGhost>
        </Container>
      </div>
    );
  }

  return (
    <>
      <HeaderOrderSuccess />
      <OrderSuccessWrapper>
        {!isPaymentCreditCard && (
          <Layout>
            <OrderSuccessTitle isPickupStore={isPickupStore} orderCode={code} {...options} />
          </Layout>
        )}
        <ContentBuilder
          components={components}
          match={match}
          widgetsMapper={widgetViewsMapperPayment[view]}
        />
      </OrderSuccessWrapper>
    </>
  );
};

export default OrderSuccessContainer;
